import { memo, useCallback, useRef } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';

import Loader from 'src/components/common/loader';

import { doPost } from 'src/api';
import { ApiResponseStatus, ApiRoute, ErrorText } from 'src/constants';
import type { IPaypalAcceptPaymentResponse } from 'src/interfaces';
import type { PaypalButtonProps } from './paypal-button.props';

import './paypal-button.scss';

const PaypalButton = ({
  isDisabled,
  onCancel,
  onCreateOrder,
  onError,
  onSuccess,
}: PaypalButtonProps) => {
  const [{ isPending }] = usePayPalScriptReducer();
  const orderIdRef = useRef<string>();

  const handleCreateOrder = useCallback(async () => {
    onError('');

    try {
      const { id, paypal_order_id } = await onCreateOrder();
      orderIdRef.current = id;
      return paypal_order_id;
    } catch (err) {
      console.error(err);
      onError(ErrorText.DEFAULT);
      return '';
    }
  }, [onCreateOrder, onError]);

  const handleApprove = useCallback(async () => {
    try {
      const orderId = orderIdRef.current;
      if (!orderId) {
        console.error('Order id is not defined');
        return onError(ErrorText.DEFAULT);
      }

      const result = await doPost<IPaypalAcceptPaymentResponse>(
        `${ApiRoute.ORDERS}/${orderId}/payments/paypal-capture`,
        null
      );

      if (result.status === ApiResponseStatus.SUCCESS) {
        onSuccess();
      }
    } catch (err) {
      console.error(err);
      onError(ErrorText.DEFAULT);
    }
  }, [onError, onSuccess]);

  const handleError = useCallback(
    (err: Record<string, unknown>) => {
      console.error(err);
      onError(ErrorText.DEFAULT);
    },
    [onError]
  );

  return (
    <div className="paypal-button">
      {isPending ? <Loader /> : null}
      <PayPalButtons
        disabled={isDisabled}
        forceReRender={[handleApprove, handleCreateOrder, handleError, onCancel]}
        style={{
          color: 'black',
          height: 52,
          label: 'pay',
          layout: 'horizontal',
          tagline: true,
        }}
        createOrder={handleCreateOrder}
        onApprove={handleApprove}
        onCancel={onCancel}
        onError={handleError}
      />
    </div>
  );
};

export default memo(PaypalButton);
